import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  ShoppingCart, Package, RotateCcw, AlertTriangle, BarChart3, Settings, Plus, 
  Search, Trash2, Edit, RefreshCw, Download, CheckCircle, XCircle, Clock,
  TrendingUp, TrendingDown, AlertCircle, Award, Trophy, Medal, Upload, DollarSign,
  FileSpreadsheet, Wallet
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = ["Amazon", "Flipkart", "Website", "Meesho", "Other"];
const FALSE_RETURN_REASONS = [
  "Wrong product returned",
  "Used product",
  "Damaged by customer",
  "Short quantity",
  "Other"
];

export default function OnlineSalesPage() {
  const [activeTab, setActiveTab] = useState("goods");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Get user info for permission checks
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userDept = user?.department?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin';
  const isAccounts = userDept === 'accounts';
  const canSeePrices = isAdmin || isAccounts; // Only admin and accounts can see prices/P&L
  
  // Data states
  const [goods, setGoods] = useState([]);
  const [sales, setSales] = useState([]);
  const [returns, setReturns] = useState([]);
  const [disputed, setDisputed] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [settings, setSettings] = useState({ low_stock_threshold: 30, high_return_percent: 10 });
  
  // Dialog states
  const [showAddGoodsDialog, setShowAddGoodsDialog] = useState(false);
  const [showEditGoodsDialog, setShowEditGoodsDialog] = useState(false);
  const [showAddStockDialog, setShowAddStockDialog] = useState(false);
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [showEditSaleDialog, setShowEditSaleDialog] = useState(false);
  const [showEditReturnDialog, setShowEditReturnDialog] = useState(false);
  
  // Form states
  const [goodsForm, setGoodsForm] = useState({ design: "", size: "", color: "", quantity: "", cost_price: "", notes: "" });
  const [editGoodsForm, setEditGoodsForm] = useState({ id: "", design: "", size: "", color: "", quantity: "", cost_price: "", notes: "" });
  const [addStockForm, setAddStockForm] = useState({ quantity: "", notes: "" });
  const [saleForm, setSaleForm] = useState({ platform: "", design: "", size: "", color: "", quantity_sold: "", sale_price: "", dispatch_date: "", order_id: "", notes: "" });
  const [returnForm, setReturnForm] = useState({ sale_id: "", design: "", size: "", color: "", return_received_date: "", returned_quantity: "", return_condition: "", false_return_reason: "", claim_to_platform: false, order_id: "", notes: "" });
  const [settingsForm, setSettingsForm] = useState({ low_stock_threshold: 30, high_return_percent: 10 });
  const [editSaleForm, setEditSaleForm] = useState({ id: "", platform: "", order_id: "", quantity_sold: "", dispatch_date: "", notes: "" });
  const [editReturnForm, setEditReturnForm] = useState({ id: "", order_id: "", returned_quantity: "", return_received_date: "", return_condition: "", false_return_reason: "", notes: "" });
  
  // Orphan cleanup state
  const [orphanData, setOrphanData] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  
  // Selected items
  const [selectedGoods, setSelectedGoods] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  
  // Search
  const [searchQuery, setSearchQuery] = useState("");
  
  // Sales Trends data
  const [salesTrends, setSalesTrends] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  
  // P&L and Upload states
  const [pnlData, setPnlData] = useState(null);
  const [loadingPnl, setLoadingPnl] = useState(false);
  const [uploadHistory, setUploadHistory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [uploadPlatform, setUploadPlatform] = useState('auto');
  const [deleting, setDeleting] = useState(false);

  // Extract unique designs, colors, and design-size mappings from goods
  const uniqueDesigns = [...new Set(goods.map(g => g.design))].filter(Boolean).sort();
  const uniqueColors = [...new Set(goods.map(g => g.color))].filter(Boolean).sort();
  
  // Get sizes for a specific design
  const getSizesForDesign = (design) => {
    return [...new Set(goods.filter(g => g.design === design).map(g => g.size))].filter(Boolean).sort();
  };
  
  // Get colors for a specific design
  const getColorsForDesign = (design) => {
    return [...new Set(goods.filter(g => g.design === design).map(g => g.color))].filter(Boolean).sort();
  };

  // Fetch functions
  const fetchGoods = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/goods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoods(response.data || []);
    } catch (err) {
      console.error("Failed to fetch goods:", err);
    }
  }, []);

  const fetchSales = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data || []);
    } catch (err) {
      console.error("Failed to fetch sales:", err);
    }
  }, []);

  const fetchReturns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/returns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReturns(response.data || []);
    } catch (err) {
      console.error("Failed to fetch returns:", err);
    }
  }, []);

  const fetchDisputed = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/disputed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisputed(response.data || []);
    } catch (err) {
      console.error("Failed to fetch disputed:", err);
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
      setSettings(response.data.settings || { low_stock_threshold: 20, high_return_percent: 10 });
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.error("Failed to fetch alerts:", err);
    }
  }, []);

  const fetchSalesTrends = useCallback(async () => {
    setLoadingTrends(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/sku-sales-trends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesTrends(response.data);
    } catch (err) {
      console.error("Failed to fetch sales trends:", err);
    } finally {
      setLoadingTrends(false);
    }
  }, []);

  const fetchPnL = useCallback(async () => {
    setLoadingPnl(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/pnl`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPnlData(response.data);
    } catch (err) {
      console.error("Failed to fetch P&L:", err);
    } finally {
      setLoadingPnl(false);
    }
  }, []);

  const fetchUploadHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/upload-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadHistory(response.data);
    } catch (err) {
      console.error("Failed to fetch upload history:", err);
    }
  }, []);

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadType(type);
    setError("");
    setSuccess("");
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('platform', uploadPlatform);
      
      const endpoint = type === 'orders' ? 'upload/orders' : 
                      type === 'returns' ? 'upload/returns' : 'upload/payments';
      
      const response = await axios.post(`${API_URL}/api/ecom/${endpoint}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded: ${response.data.imported} imported, ${response.data.skipped || 0} skipped`);
      
      // Refresh data
      fetchAll();
      fetchUploadHistory();
      if (type === 'payments') fetchPnL();
      
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to upload ${type}`);
    } finally {
      setUploading(false);
      setUploadType('');
      e.target.value = ''; // Reset file input
    }
  };

  const handleDeleteImportedSales = async () => {
    if (!window.confirm("Are you sure you want to delete ALL imported sales? Stock will be restored.")) return;
    setDeleting(true);
    setError("");
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/ecom/sales/bulk/imported`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Deleted ${response.data.deleted_count} imported sales. Stock restored.`);
      fetchAll();
      fetchUploadHistory();
      fetchPnL();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete imported sales");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteImportedReturns = async () => {
    if (!window.confirm("Are you sure you want to delete ALL imported returns? Stock adjustments will be reversed.")) return;
    setDeleting(true);
    setError("");
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/ecom/returns/bulk/imported`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Deleted ${response.data.deleted_count} imported returns.`);
      fetchAll();
      fetchUploadHistory();
      fetchPnL();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete imported returns");
    } finally {
      setDeleting(false);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchGoods(), fetchSales(), fetchReturns(), fetchDisputed(), fetchDashboard(), fetchAlerts()]);
    setLoading(false);
  }, [fetchGoods, fetchSales, fetchReturns, fetchDisputed, fetchDashboard, fetchAlerts]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Handlers
  const handleAddGoods = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/ecom/goods`, {
        design: goodsForm.design,
        size: goodsForm.size,
        color: goodsForm.color,
        quantity: parseInt(goodsForm.quantity),
        cost_price: parseFloat(goodsForm.cost_price) || 0,
        notes: goodsForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("ECOM goods added successfully");
      setShowAddGoodsDialog(false);
      setGoodsForm({ design: "", size: "", color: "", quantity: "", cost_price: "", notes: "" });
      fetchGoods();
      fetchDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add goods");
    }
  };

  const openEditGoodsDialog = (item) => {
    setEditGoodsForm({
      id: item.id,
      design: item.design,
      size: item.size,
      color: item.color,
      quantity: item.quantity,
      cost_price: item.cost_price || "",
      notes: item.notes || ""
    });
    setShowEditGoodsDialog(true);
  };

  const handleEditGoods = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        design: editGoodsForm.design,
        size: editGoodsForm.size,
        color: editGoodsForm.color,
        quantity: parseInt(editGoodsForm.quantity),
        notes: editGoodsForm.notes
      };
      // Only include cost_price if user can see prices
      if (canSeePrices && editGoodsForm.cost_price) {
        updateData.cost_price = parseFloat(editGoodsForm.cost_price);
      }
      await axios.put(`${API_URL}/api/ecom/goods/${editGoodsForm.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("ECOM goods updated successfully");
      setShowEditGoodsDialog(false);
      setEditGoodsForm({ id: "", design: "", size: "", color: "", quantity: "", cost_price: "", notes: "" });
      fetchGoods();
      fetchDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update goods");
    }
  };

  const handleAddStock = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/ecom/goods/${selectedGoods.id}/add-stock`, {
        quantity: parseInt(addStockForm.quantity),
        notes: addStockForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Added ${addStockForm.quantity} units to ${selectedGoods.sku}`);
      setShowAddStockDialog(false);
      setAddStockForm({ quantity: "", notes: "" });
      setSelectedGoods(null);
      fetchGoods();
      fetchDashboard();
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add stock");
    }
  };

  const handleDeleteGoods = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/ecom/goods/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Item deleted successfully");
      fetchGoods();
      fetchDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete item");
    }
  };

  const handleAddSale = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/ecom/sales`, {
        platform: saleForm.platform,
        design: saleForm.design,
        size: saleForm.size,
        color: saleForm.color,
        quantity_sold: parseInt(saleForm.quantity_sold),
        dispatch_date: saleForm.dispatch_date,
        order_id: saleForm.order_id,
        notes: saleForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Sale recorded successfully");
      setShowSaleDialog(false);
      setSaleForm({ platform: "", design: "", size: "", color: "", quantity_sold: "", dispatch_date: "", order_id: "", notes: "" });
      fetchSales();
      fetchGoods();
      fetchDashboard();
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to record sale");
    }
  };

  const handleDeleteSale = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sale? Stock will be restored and associated returns will be deleted.")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/ecom/sales/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Sale deleted, returns removed, and stock restored");
      fetchSales();
      fetchReturns();  // Refresh returns since they were deleted
      fetchDisputed(); // Refresh disputed items
      fetchGoods();
      fetchDashboard();
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete sale");
    }
  };

  const openReturnDialog = (sale) => {
    setSelectedSale(sale);
    setReturnForm({
      sale_id: sale.id,
      design: sale.design,
      size: sale.size,
      color: sale.color,
      return_received_date: new Date().toISOString().split('T')[0],
      returned_quantity: "",
      return_condition: "",
      false_return_reason: "",
      claim_to_platform: false,
      order_id: sale.order_id || "",
      notes: ""
    });
    setShowReturnDialog(true);
  };

  const handleAddReturn = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/ecom/returns`, returnForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Return recorded successfully");
      setShowReturnDialog(false);
      setReturnForm({ sale_id: "", design: "", size: "", color: "", return_received_date: "", returned_quantity: "", return_condition: "", false_return_reason: "", claim_to_platform: false, order_id: "", notes: "" });
      setSelectedSale(null);
      fetchReturns();
      fetchSales();
      fetchGoods();
      fetchDisputed();
      fetchDashboard();
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to record return");
    }
  };

  const handleUpdateClaimStatus = async (disputedId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/ecom/disputed/${disputedId}/claim-status`, {
        claim_status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Claim status updated");
      fetchDisputed();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update claim status");
    }
  };

  const openEditSaleDialog = (sale) => {
    setEditSaleForm({
      id: sale.id,
      platform: sale.platform,
      order_id: sale.order_id || "",
      quantity_sold: sale.quantity_sold,
      dispatch_date: sale.dispatch_date || "",
      notes: sale.notes || ""
    });
    setShowEditSaleDialog(true);
  };

  const handleEditSale = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/ecom/sales/${editSaleForm.id}`, {
        platform: editSaleForm.platform,
        order_id: editSaleForm.order_id,
        quantity_sold: parseInt(editSaleForm.quantity_sold),
        dispatch_date: editSaleForm.dispatch_date,
        notes: editSaleForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess("Sale updated successfully");
      setShowEditSaleDialog(false);
      fetchSales(); fetchGoods(); fetchDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update sale");
    }
  };

  const openEditReturnDialog = (ret) => {
    setEditReturnForm({
      id: ret.id,
      order_id: ret.order_id || "",
      returned_quantity: ret.returned_quantity,
      return_received_date: ret.return_received_date || "",
      return_condition: ret.return_condition,
      false_return_reason: ret.false_return_reason || "",
      notes: ret.notes || ""
    });
    setShowEditReturnDialog(true);
  };

  const handleEditReturn = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/ecom/returns/${editReturnForm.id}`, {
        order_id: editReturnForm.order_id,
        returned_quantity: parseInt(editReturnForm.returned_quantity),
        return_received_date: editReturnForm.return_received_date,
        return_condition: editReturnForm.return_condition,
        false_return_reason: editReturnForm.false_return_reason,
        notes: editReturnForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess("Return updated successfully");
      setShowEditReturnDialog(false);
      fetchReturns(); fetchSales(); fetchGoods(); fetchDisputed(); fetchDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update return");
    }
  };

  const handleSaveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/ecom/settings`, settingsForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Settings saved successfully");
      setShowSettingsDialog(false);
      setSettings(settingsForm);
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save settings");
    }
  };

  // Orphan data check and cleanup functions
  const checkOrphanData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/orphan-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrphanData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to check orphaned data");
    }
  };

  const cleanupOrphanData = async () => {
    if (!window.confirm("Are you sure you want to delete all orphaned returns and disputed items? This action cannot be undone.")) {
      return;
    }
    
    setCleaningUp(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/ecom/cleanup-orphans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Cleanup complete: ${response.data.orphaned_returns_deleted} orphaned returns and ${response.data.orphaned_disputed_deleted} orphaned disputed items deleted.`);
      setOrphanData(null);
      // Refresh data
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to cleanup orphaned data");
    } finally {
      setCleaningUp(false);
    }
  };

  // Excel download functions
  const downloadExcel = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ecom/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess(`${type} data downloaded successfully`);
    } catch (err) {
      setError(`Failed to download ${type} data`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const filteredGoods = goods.filter(g => 
    g.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.design?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.color?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSales = sales.filter(s =>
    s.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.platform?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.order_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredReturns = returns.filter(r =>
    r.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.platform?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.return_condition?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDisputed = disputed.filter(d =>
    d.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.platform?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.false_return_reason?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="online-sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Online Sales</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage ECOM inventory, sales, returns, and analytics</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchAll} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => { setSettingsForm(settings); setShowSettingsDialog(true); }} data-testid="settings-btn">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">{success}</div>}

      {/* Summary Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <Package className="w-5 h-5 text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{dashboard.summary.total_inventory}</p>
              <p className="text-xs text-muted-foreground">Total Inventory</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <ShoppingCart className="w-5 h-5 text-green-500 mb-2" />
              <p className="text-2xl font-bold">{dashboard.summary.total_quantity_sold}</p>
              <p className="text-xs text-muted-foreground">Units Sold</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <RotateCcw className="w-5 h-5 text-orange-500 mb-2" />
              <p className="text-2xl font-bold">{dashboard.summary.return_percent}%</p>
              <p className="text-xs text-muted-foreground">Return Rate</p>
            </CardContent>
          </Card>
          <Card className={dashboard.summary.false_return_percent > 0 ? "border-red-500" : ""}>
            <CardContent className="p-4">
              <XCircle className="w-5 h-5 text-red-500 mb-2" />
              <p className="text-2xl font-bold">{dashboard.summary.false_return_percent}%</p>
              <p className="text-xs text-muted-foreground">False Return %</p>
            </CardContent>
          </Card>
          <Card className={dashboard.summary.low_stock_count > 0 ? "border-yellow-500" : ""}>
            <CardContent className="p-4">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mb-2" />
              <p className="text-2xl font-bold">{dashboard.summary.low_stock_count}</p>
              <p className="text-xs text-muted-foreground">Low Stock SKUs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <AlertCircle className="w-5 h-5 text-purple-500 mb-2" />
              <p className="text-2xl font-bold">{alerts.length}</p>
              <p className="text-xs text-muted-foreground">Alerts</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <span className="font-semibold">Active Alerts ({alerts.length})</span>
              {alerts.length > 3 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-auto text-yellow-700 hover:text-yellow-800"
                  onClick={() => setShowAllAlerts(!showAllAlerts)}
                >
                  {showAllAlerts ? 'Show Less' : `Show All (${alerts.length})`}
                </Button>
              )}
            </div>
            <div className="space-y-1">
              {(showAllAlerts ? alerts : alerts.slice(0, 3)).map((alert, idx) => (
                <div key={idx} className={`text-sm p-2 rounded ${alert.severity === 'high' ? 'bg-red-100 dark:bg-red-900' : 'bg-yellow-100 dark:bg-yellow-900'}`}>
                  <Badge variant={alert.severity === 'high' ? 'destructive' : 'secondary'} className="mr-2">{alert.type.replace(/_/g, ' ')}</Badge>
                  {alert.message}
                </div>
              ))}
              {!showAllAlerts && alerts.length > 3 && (
                <p 
                  className="text-sm text-yellow-700 cursor-pointer hover:underline font-medium"
                  onClick={() => setShowAllAlerts(true)}
                >
                  ...click to see {alerts.length - 3} more alerts
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={(val) => {
        setActiveTab(val);
        if (val === 'trends' && !salesTrends) {
          fetchSalesTrends();
        }
        if (val === 'pnl' && !pnlData && canSeePrices) {
          fetchPnL();
          fetchUploadHistory();
        }
      }}>
        <TabsList className={`grid w-full ${canSeePrices ? 'grid-cols-4 sm:grid-cols-7' : 'grid-cols-4 sm:grid-cols-6'} lg:w-auto lg:inline-grid`}>
          <TabsTrigger value="goods" className="text-xs sm:text-sm">
            <Package className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">ECOM</span> Goods
          </TabsTrigger>
          <TabsTrigger value="sales" className="text-xs sm:text-sm">
            <ShoppingCart className="w-4 h-4 mr-1 sm:mr-2" />
            Sales
          </TabsTrigger>
          <TabsTrigger value="returns" className="text-xs sm:text-sm">
            <RotateCcw className="w-4 h-4 mr-1 sm:mr-2" />
            Returns
          </TabsTrigger>
          <TabsTrigger value="disputed" className="text-xs sm:text-sm">
            <XCircle className="w-4 h-4 mr-1 sm:mr-2" />
            Disputed
          </TabsTrigger>
          {canSeePrices && (
            <TabsTrigger value="pnl" className="text-xs sm:text-sm">
              <Wallet className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">P&L</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="trends" className="text-xs sm:text-sm">
            <Trophy className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Sales</span> Trends
          </TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs sm:text-sm">
            <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* ECOM Goods Tab */}
        <TabsContent value="goods" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle>ECOM Goods Inventory</CardTitle>
                <CardDescription>Manage finished goods inventory for online sales</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => downloadExcel('goods')} data-testid="download-goods-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Excel
                </Button>
                <Button onClick={() => setShowAddGoodsDialog(true)} data-testid="add-goods-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Inventory
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by SKU, design, color..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Color</TableHead>
                      <TableHead className="text-center">Quantity</TableHead>
                      {canSeePrices && <TableHead className="text-right">Cost Price</TableHead>}
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredGoods.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canSeePrices ? 7 : 6} className="text-center py-8 text-muted-foreground">
                          No inventory found. Add your first item!
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredGoods.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-mono font-medium">{item.sku}</TableCell>
                          <TableCell>{item.design}</TableCell>
                          <TableCell>{item.size}</TableCell>
                          <TableCell>{item.color}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={item.quantity <= settings.low_stock_threshold ? (item.quantity === 0 ? 'destructive' : 'secondary') : 'outline'}>
                              {item.quantity}
                            </Badge>
                          </TableCell>
                          {canSeePrices && (
                            <TableCell className="text-right font-medium">
                              {item.cost_price ? `₹${item.cost_price.toLocaleString()}` : '-'}
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedGoods(item); setAddStockForm({ quantity: "", notes: "" }); setShowAddStockDialog(true); }} data-testid={`add-stock-${item.sku}`}>
                                <Plus className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openEditGoodsDialog(item)} data-testid={`edit-goods-${item.sku}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              {isAdmin && (
                                <Button size="sm" variant="destructive" onClick={() => handleDeleteGoods(item.id)} data-testid={`delete-goods-${item.sku}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle>Online Sales</CardTitle>
                <CardDescription>Record dispatched orders - stock will be reduced automatically</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => downloadExcel('sales')} data-testid="download-sales-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Excel
                </Button>
                <Button onClick={() => { setSaleForm({ ...saleForm, dispatch_date: new Date().toISOString().split('T')[0] }); setShowSaleDialog(true); }} data-testid="add-sale-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Record Sale
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by SKU, platform, order ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="sales-search"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Order ID</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead className="text-center">Qty Sold</TableHead>
                      <TableHead className="text-center">Returned</TableHead>
                      <TableHead>Dispatch Date</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSales.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No sales recorded yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSales.map((sale) => (
                        <TableRow key={sale.id} className={sale.imported ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''}>
                          <TableCell>
                            <Badge variant="outline">{sale.platform}</Badge>
                          </TableCell>
                          <TableCell className="font-mono">{sale.order_id || '-'}</TableCell>
                          <TableCell className="font-mono">{sale.sku}</TableCell>
                          <TableCell className="text-center">{sale.quantity_sold}</TableCell>
                          <TableCell className="text-center">
                            {sale.returned_quantity > 0 ? (
                              <Badge variant="secondary">{sale.returned_quantity}</Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{formatDate(sale.dispatch_date)}</TableCell>
                          <TableCell>
                            {sale.imported ? (
                              <Badge variant="secondary" className="text-xs">Excel</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">Manual</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" onClick={() => openReturnDialog(sale)} disabled={sale.returned_quantity >= sale.quantity_sold}>
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              {isAdmin && (
                                <Button size="sm" variant="outline" onClick={() => openEditSaleDialog(sale)} data-testid={`edit-sale-${sale.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button size="sm" variant="destructive" onClick={() => handleDeleteSale(sale.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Returns Tab */}
        <TabsContent value="returns" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle>Returns</CardTitle>
                <CardDescription>All returns - correct returns add back to inventory, false returns go to disputed</CardDescription>
              </div>
              <Button variant="outline" onClick={() => downloadExcel('returns')} data-testid="download-returns-btn">
                <Download className="w-4 h-4 mr-2" />
                Excel
              </Button>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by SKU, platform, condition..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="returns-search"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead>Condition</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Received Date</TableHead>
                      {isAdmin && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReturns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">
                          No returns recorded
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredReturns.map((ret) => (
                        <TableRow key={ret.id}>
                          <TableCell>
                            <Badge variant="outline">{ret.platform}</Badge>
                          </TableCell>
                          <TableCell className="font-mono">{ret.sku}</TableCell>
                          <TableCell className="text-center">{ret.returned_quantity}</TableCell>
                          <TableCell>
                            <Badge variant={ret.return_condition === 'correct' ? 'default' : 'destructive'}>
                              {ret.return_condition === 'correct' ? 'Correct' : 'False'}
                            </Badge>
                          </TableCell>
                          <TableCell>{ret.false_return_reason || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{ret.order_id || '-'}</TableCell>
                          <TableCell>{formatDate(ret.return_received_date)}</TableCell>
                          {isAdmin && (
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => openEditReturnDialog(ret)} data-testid={`edit-return-${ret.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Disputed Stock Tab */}
        <TabsContent value="disputed" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle>Disputed / Rejected Stock</CardTitle>
                <CardDescription>False returns that need claim processing</CardDescription>
              </div>
              <Button variant="outline" onClick={() => downloadExcel('disputed')} data-testid="download-disputed-btn">
                <Download className="w-4 h-4 mr-2" />
                Excel
              </Button>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by SKU, platform, reason..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="disputed-search"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Order ID</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Claim Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDisputed.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No disputed stock
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDisputed.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Badge variant="outline">{item.platform}</Badge>
                          </TableCell>
                          <TableCell className="font-mono">{item.order_id || '-'}</TableCell>
                          <TableCell className="font-mono">{item.sku}</TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell>{item.reason}</TableCell>
                          <TableCell>
                            <Badge variant={
                              item.claim_status === 'approved' ? 'default' :
                              item.claim_status === 'rejected' ? 'destructive' :
                              item.claim_status === 'pending' ? 'secondary' : 'outline'
                            }>
                              {item.claim_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Select value={item.claim_status} onValueChange={(val) => handleUpdateClaimStatus(item.id, val)}>
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                                <SelectItem value="not_claimed">Not Claimed</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* P&L Tab - Only visible to Admin and Accounts */}
        {canSeePrices && (
        <TabsContent value="pnl" className="mt-4">
          <div className="space-y-6">
            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload Platform Data
                </CardTitle>
                <CardDescription>
                  Upload Excel files directly from Amazon, Flipkart, or Meesho
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label>Platform</Label>
                    <Select value={uploadPlatform} onValueChange={setUploadPlatform}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto-detect</SelectItem>
                        <SelectItem value="Amazon">Amazon</SelectItem>
                        <SelectItem value="Flipkart">Flipkart</SelectItem>
                        <SelectItem value="Meesho">Meesho</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Orders Upload */}
                  <div className="border rounded-lg p-4 text-center">
                    <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                    <p className="font-medium mb-2">Orders Report</p>
                    <p className="text-xs text-muted-foreground mb-3">Amazon Order Report / Flipkart Orders / Meesho Orders</p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'orders')}
                        disabled={uploading}
                      />
                      <Button variant="outline" size="sm" disabled={uploading} asChild>
                        <span>
                          {uploading && uploadType === 'orders' ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload Orders
                        </span>
                      </Button>
                    </label>
                  </div>
                  
                  {/* Returns Upload */}
                  <div className="border rounded-lg p-4 text-center">
                    <RotateCcw className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                    <p className="font-medium mb-2">Returns Report</p>
                    <p className="text-xs text-muted-foreground mb-3">Amazon Returns / Flipkart RTO / Meesho Returns</p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'returns')}
                        disabled={uploading}
                      />
                      <Button variant="outline" size="sm" disabled={uploading} asChild>
                        <span>
                          {uploading && uploadType === 'returns' ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload Returns
                        </span>
                      </Button>
                    </label>
                  </div>
                  
                  {/* Payments Upload */}
                  <div className="border rounded-lg p-4 text-center">
                    <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="font-medium mb-2">Payment/Settlement</p>
                    <p className="text-xs text-muted-foreground mb-3">Amazon Payment Report / Flipkart Settlement</p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'payments')}
                        disabled={uploading}
                      />
                      <Button variant="outline" size="sm" disabled={uploading} asChild>
                        <span>
                          {uploading && uploadType === 'payments' ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload Payments
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
                
                {/* Upload History */}
                {uploadHistory && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                      <p className="text-sm font-medium">Import Summary</p>
                      {isAdmin && (uploadHistory.imported_sales > 0 || uploadHistory.imported_returns > 0) && (
                        <div className="flex gap-2">
                          {uploadHistory.imported_sales > 0 && (
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={handleDeleteImportedSales}
                              disabled={deleting}
                            >
                              {deleting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                              Delete Sales ({uploadHistory.imported_sales})
                            </Button>
                          )}
                          {uploadHistory.imported_returns > 0 && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={handleDeleteImportedReturns}
                              disabled={deleting}
                              className="text-destructive border-destructive hover:bg-destructive/10"
                            >
                              {deleting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                              Delete Returns ({uploadHistory.imported_returns})
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span>📦 Imported Sales: {uploadHistory.imported_sales}</span>
                      <span>↩️ Imported Returns: {uploadHistory.imported_returns}</span>
                      <span>💰 Payment Records: {uploadHistory.payment_records}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* P&L Summary */}
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Profit & Loss Statement</h2>
              <Button variant="outline" onClick={fetchPnL} disabled={loadingPnl}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingPnl ? 'animate-spin' : ''}`} />
                Refresh P&L
              </Button>
            </div>

            {loadingPnl ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : pnlData ? (
              <>
                {/* P&L Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Gross Revenue</p>
                      <p className="text-2xl font-bold">₹{pnlData.summary.gross_revenue.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-red-500">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Cost of Goods</p>
                      <p className="text-2xl font-bold text-red-600">₹{pnlData.summary.cost_of_goods.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-yellow-500">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Platform Fees</p>
                      <p className="text-2xl font-bold text-yellow-600">₹{pnlData.summary.platform_fees.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className={`border-l-4 ${pnlData.summary.net_profit >= 0 ? 'border-l-green-500' : 'border-l-red-500'}`}>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Net Profit</p>
                      <p className={`text-2xl font-bold ${pnlData.summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ₹{pnlData.summary.net_profit.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Margin: {pnlData.summary.profit_margin}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Platform-wise P&L */}
                <Card>
                  <CardHeader>
                    <CardTitle>Platform-wise P&L</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Platform</TableHead>
                            <TableHead className="text-right">Orders</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Gross Revenue</TableHead>
                            <TableHead className="text-right">Cost</TableHead>
                            <TableHead className="text-right">Fees</TableHead>
                            <TableHead className="text-right">Returns</TableHead>
                            <TableHead className="text-right">Profit</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pnlData.platform_wise.map((plat) => (
                            <TableRow key={plat.platform}>
                              <TableCell className="font-medium">{plat.platform}</TableCell>
                              <TableCell className="text-right">{plat.total_orders}</TableCell>
                              <TableCell className="text-right">{plat.total_quantity}</TableCell>
                              <TableCell className="text-right">₹{plat.gross_revenue.toLocaleString()}</TableCell>
                              <TableCell className="text-right text-red-600">₹{plat.cost_of_goods.toLocaleString()}</TableCell>
                              <TableCell className="text-right text-yellow-600">₹{plat.platform_fees.toLocaleString()}</TableCell>
                              <TableCell className="text-right text-orange-600">₹{plat.returns_value.toLocaleString()}</TableCell>
                              <TableCell className={`text-right font-bold ${plat.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ₹{plat.profit.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* Top SKUs by Profit */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top SKUs by Profit</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>SKU</TableHead>
                            <TableHead className="text-right">Sold</TableHead>
                            <TableHead className="text-right">Returned</TableHead>
                            <TableHead className="text-right">Revenue</TableHead>
                            <TableHead className="text-right">Cost</TableHead>
                            <TableHead className="text-right">Profit</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pnlData.sku_wise.slice(0, 10).map((sku) => (
                            <TableRow key={sku.sku}>
                              <TableCell className="font-mono">{sku.sku}</TableCell>
                              <TableCell className="text-right">{sku.total_sold}</TableCell>
                              <TableCell className="text-right">{sku.returned}</TableCell>
                              <TableCell className="text-right">₹{sku.gross_revenue.toLocaleString()}</TableCell>
                              <TableCell className="text-right text-red-600">₹{sku.cost_of_goods.toLocaleString()}</TableCell>
                              <TableCell className={`text-right font-bold ${sku.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ₹{sku.profit.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Click "Refresh P&L" to load profit & loss data</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Upload payment/settlement reports for accurate fee calculations
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        )}

        {/* Sales Trends Tab */}
        <TabsContent value="trends" className="mt-4">
          <div className="space-y-6">
            {/* Summary Cards */}
            {salesTrends && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Total SKUs</p>
                    <p className="text-2xl font-bold">{salesTrends.summary.total_skus}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Active SKUs</p>
                    <p className="text-2xl font-bold text-green-600">{salesTrends.summary.active_skus}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Critical Stock</p>
                    <p className="text-2xl font-bold text-red-600">{salesTrends.summary.critical_stock_skus}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-yellow-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Low Stock</p>
                    <p className="text-2xl font-bold text-yellow-600">{salesTrends.summary.low_stock_skus}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Refresh Button */}
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">SKU Sales Rankings & Inventory Analysis</h2>
              <Button variant="outline" onClick={fetchSalesTrends} disabled={loadingTrends}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingTrends ? 'animate-spin' : ''}`} />
                Refresh Trends
              </Button>
            </div>

            {loadingTrends ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : salesTrends ? (
              <>
                {/* Top Selling SKUs Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-500" />
                      Top Selling SKUs - Sales Rankings
                    </CardTitle>
                    <CardDescription>
                      SKUs ranked by total quantity sold. Use this to prioritize inventory restocking.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search SKU, design, size, color..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                          data-testid="trends-search"
                        />
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-16 text-center">Rank</TableHead>
                            <TableHead>SKU</TableHead>
                            <TableHead>Design</TableHead>
                            <TableHead className="text-center">Total Sold</TableHead>
                            <TableHead className="text-center">Net Sold</TableHead>
                            <TableHead className="text-center">Return %</TableHead>
                            <TableHead className="text-center">Current Stock</TableHead>
                            <TableHead className="text-center">Avg Monthly</TableHead>
                            <TableHead className="text-center">Months Left</TableHead>
                            <TableHead>Recommendation</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {salesTrends.sku_rankings
                            .filter(item => 
                              item.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              item.design?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              item.size?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              item.color?.toLowerCase().includes(searchQuery.toLowerCase())
                            )
                            .map((item) => (
                            <TableRow 
                              key={item.sku} 
                              className={
                                item.urgency === 'critical' ? 'bg-red-50 dark:bg-red-950' :
                                item.urgency === 'high' ? 'bg-yellow-50 dark:bg-yellow-950' :
                                item.rank <= 3 ? 'bg-amber-50 dark:bg-amber-950' : ''
                              }
                            >
                              <TableCell className="text-center">
                                {item.rank === 1 ? (
                                  <div className="flex justify-center">
                                    <Trophy className="w-5 h-5 text-yellow-500" />
                                  </div>
                                ) : item.rank === 2 ? (
                                  <div className="flex justify-center">
                                    <Medal className="w-5 h-5 text-gray-400" />
                                  </div>
                                ) : item.rank === 3 ? (
                                  <div className="flex justify-center">
                                    <Award className="w-5 h-5 text-amber-600" />
                                  </div>
                                ) : (
                                  <span className="font-semibold text-muted-foreground">#{item.rank}</span>
                                )}
                              </TableCell>
                              <TableCell className="font-mono font-medium">{item.sku}</TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <p className="font-medium">{item.design}</p>
                                  <p className="text-muted-foreground text-xs">{item.size} • {item.color}</p>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="font-bold text-lg">{item.total_sold}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="font-semibold">{item.net_sold}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant={item.return_rate > 10 ? 'destructive' : 'outline'}>
                                  {item.return_rate}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`font-bold ${item.current_stock === 0 ? 'text-red-600' : item.current_stock <= 30 ? 'text-yellow-600' : 'text-green-600'}`}>
                                  {item.current_stock}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                {item.avg_monthly_sales > 0 ? (
                                  <span>{item.avg_monthly_sales}/mo</span>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-center">
                                {item.months_of_stock < 999 ? (
                                  <Badge variant={
                                    item.months_of_stock < 1 ? 'destructive' :
                                    item.months_of_stock < 2 ? 'secondary' : 'outline'
                                  }>
                                    {item.months_of_stock} mo
                                  </Badge>
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  variant={
                                    item.urgency === 'critical' ? 'destructive' :
                                    item.urgency === 'high' ? 'secondary' :
                                    item.urgency === 'medium' ? 'outline' : 'default'
                                  }
                                  className="text-xs whitespace-nowrap"
                                >
                                  {item.reorder_recommendation}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* Platform Performance */}
                {salesTrends.summary.top_platforms?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-500" />
                        Top Performing Platforms
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {salesTrends.summary.top_platforms.map(([platform, qty], idx) => (
                          <div key={platform} className="text-center p-4 rounded-lg bg-muted/50">
                            <p className="text-2xl font-bold">{qty}</p>
                            <p className="text-sm text-muted-foreground">{platform}</p>
                            {idx === 0 && <Badge className="mt-2 bg-yellow-500">Top Platform</Badge>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Trophy className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Click "Refresh Trends" to load sales analysis</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-4">
          <div className="space-y-6">
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => downloadExcel('analytics')} data-testid="download-analytics-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Analytics Excel
              </Button>
            </div>
            {/* Platform Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>Platform-wise Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Platform</TableHead>
                        <TableHead className="text-center">Sales</TableHead>
                        <TableHead className="text-center">Qty Sold</TableHead>
                        <TableHead className="text-center">Returns</TableHead>
                        <TableHead className="text-center">False Returns</TableHead>
                        <TableHead className="text-center">Return %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboard && Object.entries(dashboard.platform_stats).map(([platform, stats]) => {
                        const returnPercent = stats.quantity > 0 ? (stats.returns / stats.quantity * 100).toFixed(1) : 0;
                        return (
                          <TableRow key={platform}>
                            <TableCell className="font-medium">{platform}</TableCell>
                            <TableCell className="text-center">{stats.sales}</TableCell>
                            <TableCell className="text-center">{stats.quantity}</TableCell>
                            <TableCell className="text-center">{stats.returns}</TableCell>
                            <TableCell className="text-center">
                              {stats.false_returns > 0 ? (
                                <Badge variant="destructive">{stats.false_returns}</Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={returnPercent > settings.high_return_percent ? 'destructive' : 'outline'}>
                                {returnPercent}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* SKU Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>SKU-wise Return Analysis</CardTitle>
                <CardDescription>SKUs with return rate above {settings.high_return_percent}% are highlighted</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead className="text-center">Sold</TableHead>
                        <TableHead className="text-center">Returned</TableHead>
                        <TableHead className="text-center">False Returns</TableHead>
                        <TableHead className="text-center">Return %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboard && Object.entries(dashboard.sku_stats)
                        .sort((a, b) => b[1].return_percent - a[1].return_percent)
                        .map(([sku, stats]) => (
                          <TableRow key={sku} className={stats.return_percent > settings.high_return_percent ? 'bg-red-50 dark:bg-red-950' : ''}>
                            <TableCell className="font-mono font-medium">{sku}</TableCell>
                            <TableCell className="text-center">{stats.sold}</TableCell>
                            <TableCell className="text-center">{stats.returned}</TableCell>
                            <TableCell className="text-center">
                              {stats.false_returned > 0 ? (
                                <Badge variant="destructive">{stats.false_returned}</Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={stats.return_percent > settings.high_return_percent ? 'destructive' : 'outline'}>
                                {stats.return_percent.toFixed(1)}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Goods Dialog */}
      <Dialog open={showAddGoodsDialog} onOpenChange={setShowAddGoodsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add ECOM Goods</DialogTitle>
            <DialogDescription>Add new inventory to ECOM goods</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Design *</Label>
              <Select 
                value={goodsForm.design} 
                onValueChange={(val) => {
                  const sizes = getSizesForDesign(val);
                  setGoodsForm({
                    ...goodsForm, 
                    design: val,
                    size: sizes.length === 1 ? sizes[0] : goodsForm.size
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select or type design" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueDesigns.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input 
                className="mt-2" 
                value={goodsForm.design} 
                onChange={(e) => setGoodsForm({...goodsForm, design: e.target.value.toUpperCase()})} 
                placeholder="Or type new design name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Size *</Label>
                {goodsForm.design && getSizesForDesign(goodsForm.design).length > 0 ? (
                  <Select 
                    value={goodsForm.size} 
                    onValueChange={(val) => setGoodsForm({...goodsForm, size: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {getSizesForDesign(goodsForm.design).map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={goodsForm.size} onChange={(e) => setGoodsForm({...goodsForm, size: e.target.value})} placeholder="e.g., 30x60" />
                )}
              </div>
              <div>
                <Label>Color *</Label>
                <Select 
                  value={goodsForm.color} 
                  onValueChange={(val) => setGoodsForm({...goodsForm, color: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent>
                    {(goodsForm.design ? getColorsForDesign(goodsForm.design) : uniqueColors).map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  className="mt-2" 
                  value={goodsForm.color} 
                  onChange={(e) => setGoodsForm({...goodsForm, color: e.target.value.toUpperCase()})} 
                  placeholder="Or type new color"
                />
              </div>
            </div>
            <div className={`grid ${canSeePrices ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
              <div>
                <Label>Quantity *</Label>
                <Input type="number" value={goodsForm.quantity} onChange={(e) => setGoodsForm({...goodsForm, quantity: e.target.value})} placeholder="Enter quantity" />
              </div>
              {canSeePrices && (
                <div>
                  <Label>Cost Price (₹)</Label>
                  <Input type="number" step="0.01" value={goodsForm.cost_price} onChange={(e) => setGoodsForm({...goodsForm, cost_price: e.target.value})} placeholder="Cost per unit" />
                </div>
              )}
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={goodsForm.notes} onChange={(e) => setGoodsForm({...goodsForm, notes: e.target.value})} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddGoodsDialog(false)}>Cancel</Button>
            <Button onClick={handleAddGoods} disabled={!goodsForm.design || !goodsForm.size || !goodsForm.color || !goodsForm.quantity}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Goods Dialog */}
      <Dialog open={showEditGoodsDialog} onOpenChange={setShowEditGoodsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit ECOM Goods</DialogTitle>
            <DialogDescription>Update inventory details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Design *</Label>
              <Input value={editGoodsForm.design} onChange={(e) => setEditGoodsForm({...editGoodsForm, design: e.target.value})} placeholder="e.g., LUXURY BATH" data-testid="edit-goods-design" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Size *</Label>
                <Input value={editGoodsForm.size} onChange={(e) => setEditGoodsForm({...editGoodsForm, size: e.target.value})} placeholder="e.g., 30x60" data-testid="edit-goods-size" />
              </div>
              <div>
                <Label>Color *</Label>
                <Input value={editGoodsForm.color} onChange={(e) => setEditGoodsForm({...editGoodsForm, color: e.target.value})} placeholder="e.g., WHITE" data-testid="edit-goods-color" />
              </div>
            </div>
            <div className={`grid ${canSeePrices ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
              <div>
                <Label>Quantity *</Label>
                <Input type="number" value={editGoodsForm.quantity} onChange={(e) => setEditGoodsForm({...editGoodsForm, quantity: e.target.value})} placeholder="Enter quantity" data-testid="edit-goods-quantity" />
              </div>
              {canSeePrices && (
                <div>
                  <Label>Cost Price (₹)</Label>
                  <Input type="number" step="0.01" value={editGoodsForm.cost_price || ''} onChange={(e) => setEditGoodsForm({...editGoodsForm, cost_price: e.target.value})} placeholder="Cost per unit" data-testid="edit-goods-cost" />
                </div>
              )}
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={editGoodsForm.notes} onChange={(e) => setEditGoodsForm({...editGoodsForm, notes: e.target.value})} placeholder="Optional notes" data-testid="edit-goods-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditGoodsDialog(false)}>Cancel</Button>
            <Button onClick={handleEditGoods} disabled={!editGoodsForm.design || !editGoodsForm.size || !editGoodsForm.color || !editGoodsForm.quantity} data-testid="save-edit-goods-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stock Dialog */}
      <Dialog open={showAddStockDialog} onOpenChange={setShowAddStockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Stock</DialogTitle>
            <DialogDescription>Add inventory to {selectedGoods?.sku}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Current Stock</p>
              <p className="text-2xl font-bold">{selectedGoods?.quantity} units</p>
            </div>
            <div>
              <Label>Quantity to Add *</Label>
              <Input type="number" value={addStockForm.quantity} onChange={(e) => setAddStockForm({...addStockForm, quantity: e.target.value})} placeholder="Enter quantity" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={addStockForm.notes} onChange={(e) => setAddStockForm({...addStockForm, notes: e.target.value})} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStockDialog(false)}>Cancel</Button>
            <Button onClick={handleAddStock} disabled={!addStockForm.quantity || parseInt(addStockForm.quantity) <= 0}>Add Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sale Dialog */}
      <Dialog open={showSaleDialog} onOpenChange={setShowSaleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Sale</DialogTitle>
            <DialogDescription>Record a dispatched order - stock will be reduced</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Platform *</Label>
              <Select value={saleForm.platform} onValueChange={(val) => setSaleForm({...saleForm, platform: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order ID</Label>
              <Input value={saleForm.order_id} onChange={(e) => setSaleForm({...saleForm, order_id: e.target.value})} placeholder="e.g., AMZ-12345" />
            </div>
            <div>
              <Label>Design *</Label>
              <Select 
                value={saleForm.design} 
                onValueChange={(val) => {
                  const sizes = getSizesForDesign(val);
                  const colors = getColorsForDesign(val);
                  setSaleForm({
                    ...saleForm, 
                    design: val,
                    size: sizes.length === 1 ? sizes[0] : '',
                    color: colors.length === 1 ? colors[0] : ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select design" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueDesigns.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Size *</Label>
                <Select 
                  value={saleForm.size} 
                  onValueChange={(val) => setSaleForm({...saleForm, size: val})}
                  disabled={!saleForm.design}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={saleForm.design ? "Select size" : "Select design first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getSizesForDesign(saleForm.design).map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color *</Label>
                <Select 
                  value={saleForm.color} 
                  onValueChange={(val) => setSaleForm({...saleForm, color: val})}
                  disabled={!saleForm.design}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={saleForm.design ? "Select color" : "Select design first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getColorsForDesign(saleForm.design).map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity Sold *</Label>
                <Input type="number" value={saleForm.quantity_sold} onChange={(e) => setSaleForm({...saleForm, quantity_sold: e.target.value})} placeholder="Enter quantity" />
              </div>
              <div>
                <Label>Dispatch Date *</Label>
                <Input type="date" value={saleForm.dispatch_date} onChange={(e) => setSaleForm({...saleForm, dispatch_date: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaleDialog(false)}>Cancel</Button>
            <Button onClick={handleAddSale} disabled={!saleForm.platform || !saleForm.design || !saleForm.size || !saleForm.color || !saleForm.quantity_sold || !saleForm.dispatch_date}>
              Record Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Return</DialogTitle>
            <DialogDescription>Record a return for order {selectedSale?.order_id || selectedSale?.id?.slice(0, 8)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p><strong>SKU:</strong> {selectedSale?.sku}</p>
              <p><strong>Sold:</strong> {selectedSale?.quantity_sold} | <strong>Already Returned:</strong> {selectedSale?.returned_quantity || 0}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Returned Quantity *</Label>
                <Input 
                  type="number" 
                  value={returnForm.returned_quantity} 
                  onChange={(e) => setReturnForm({...returnForm, returned_quantity: parseInt(e.target.value) || ""})}
                  max={selectedSale?.quantity_sold - (selectedSale?.returned_quantity || 0)}
                  placeholder="Enter quantity"
                />
              </div>
              <div>
                <Label>Return Received Date *</Label>
                <Input type="date" value={returnForm.return_received_date} onChange={(e) => setReturnForm({...returnForm, return_received_date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Order ID</Label>
              <Input 
                value={returnForm.order_id} 
                onChange={(e) => setReturnForm({...returnForm, order_id: e.target.value})} 
                placeholder="e.g., AMZ-12345 / OD123456789"
                data-testid="return-order-id-input"
              />
            </div>
            <div>
              <Label>Return Condition *</Label>
              <Select value={returnForm.return_condition} onValueChange={(val) => setReturnForm({...returnForm, return_condition: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="correct">Correct Return (Add back to inventory)</SelectItem>
                  <SelectItem value="false">False Return (Move to disputed stock)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {returnForm.return_condition === 'false' && (
              <>
                <div>
                  <Label>False Return Reason *</Label>
                  <Select value={returnForm.false_return_reason} onValueChange={(val) => setReturnForm({...returnForm, false_return_reason: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {FALSE_RETURN_REASONS.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="claimPlatform" 
                    checked={returnForm.claim_to_platform}
                    onChange={(e) => setReturnForm({...returnForm, claim_to_platform: e.target.checked})}
                  />
                  <Label htmlFor="claimPlatform">Claim to Platform</Label>
                </div>
              </>
            )}
            <div>
              <Label>Notes</Label>
              <Input value={returnForm.notes} onChange={(e) => setReturnForm({...returnForm, notes: e.target.value})} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddReturn} 
              disabled={
                !returnForm.returned_quantity || 
                !returnForm.return_received_date || 
                !returnForm.return_condition ||
                (returnForm.return_condition === 'false' && !returnForm.false_return_reason)
              }
            >
              Record Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>ECOM Settings</DialogTitle>
            <DialogDescription>Configure alert thresholds and data management</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Low Stock Alert Threshold</Label>
              <Input 
                type="number" 
                value={settingsForm.low_stock_threshold} 
                onChange={(e) => setSettingsForm({...settingsForm, low_stock_threshold: parseInt(e.target.value) || 0})}
                placeholder="e.g., 20"
              />
              <p className="text-xs text-muted-foreground mt-1">Alert when stock falls to or below this level</p>
            </div>
            <div>
              <Label>High Return % Alert Threshold</Label>
              <Input 
                type="number" 
                value={settingsForm.high_return_percent} 
                onChange={(e) => setSettingsForm({...settingsForm, high_return_percent: parseInt(e.target.value) || 0})}
                placeholder="e.g., 10"
              />
              <p className="text-xs text-muted-foreground mt-1">Alert when SKU return rate exceeds this percentage</p>
            </div>
            
            {/* Data Cleanup Section - Admin Only */}
            {isAdmin && (
              <div className="border-t pt-4 mt-4">
                <Label className="text-red-600">Data Cleanup (Admin Only)</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Remove orphaned returns and disputed items that have no corresponding sale.
                </p>
                
                {!orphanData ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={checkOrphanData}
                    className="w-full"
                    data-testid="check-orphans-btn"
                  >
                    Check for Orphaned Data
                  </Button>
                ) : (
                  <div className="space-y-2 p-3 bg-gray-50 rounded-md text-sm">
                    <div className="flex justify-between">
                      <span>Valid Sales:</span>
                      <span className="font-medium">{orphanData.valid_sales_count}</span>
                    </div>
                    <div className="flex justify-between text-orange-600">
                      <span>Orphaned Returns:</span>
                      <span className="font-medium">{orphanData.orphaned_returns}</span>
                    </div>
                    <div className="flex justify-between text-orange-600">
                      <span>Orphaned Disputed:</span>
                      <span className="font-medium">{orphanData.orphaned_disputed}</span>
                    </div>
                    
                    {(orphanData.orphaned_returns > 0 || orphanData.orphaned_disputed > 0) ? (
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={cleanupOrphanData}
                        disabled={cleaningUp}
                        className="w-full mt-2"
                        data-testid="cleanup-orphans-btn"
                      >
                        {cleaningUp ? "Cleaning up..." : `Delete ${orphanData.orphaned_returns + orphanData.orphaned_disputed} Orphaned Items`}
                      </Button>
                    ) : (
                      <p className="text-green-600 text-center mt-2">✓ No orphaned data found</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSettingsDialog(false); setOrphanData(null); }}>Cancel</Button>
            <Button onClick={handleSaveSettings}>Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Dialog */}
      <Dialog open={showEditSaleDialog} onOpenChange={setShowEditSaleDialog}>
        <DialogContent aria-describedby="edit-sale-desc">
          <DialogHeader>
            <DialogTitle>Edit Sale</DialogTitle>
          </DialogHeader>
          <p id="edit-sale-desc" className="sr-only">Edit sale record details</p>
          <div className="space-y-3">
            <div>
              <Label>Platform</Label>
              <Select value={editSaleForm.platform} onValueChange={(v) => setEditSaleForm({...editSaleForm, platform: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order ID</Label>
              <Input value={editSaleForm.order_id} onChange={(e) => setEditSaleForm({...editSaleForm, order_id: e.target.value})} placeholder="Order ID" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantity Sold</Label>
                <Input type="number" value={editSaleForm.quantity_sold} onChange={(e) => setEditSaleForm({...editSaleForm, quantity_sold: e.target.value})} />
              </div>
              <div>
                <Label>Dispatch Date</Label>
                <Input type="date" value={editSaleForm.dispatch_date} onChange={(e) => setEditSaleForm({...editSaleForm, dispatch_date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={editSaleForm.notes} onChange={(e) => setEditSaleForm({...editSaleForm, notes: e.target.value})} placeholder="Notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSaleDialog(false)}>Cancel</Button>
            <Button onClick={handleEditSale} data-testid="save-edit-sale-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Return Dialog */}
      <Dialog open={showEditReturnDialog} onOpenChange={setShowEditReturnDialog}>
        <DialogContent aria-describedby="edit-return-desc">
          <DialogHeader>
            <DialogTitle>Edit Return</DialogTitle>
          </DialogHeader>
          <p id="edit-return-desc" className="sr-only">Edit return record details</p>
          <div className="space-y-3">
            <div>
              <Label>Order ID</Label>
              <Input value={editReturnForm.order_id} onChange={(e) => setEditReturnForm({...editReturnForm, order_id: e.target.value})} placeholder="Order ID" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Returned Quantity</Label>
                <Input type="number" value={editReturnForm.returned_quantity} onChange={(e) => setEditReturnForm({...editReturnForm, returned_quantity: e.target.value})} />
              </div>
              <div>
                <Label>Received Date</Label>
                <Input type="date" value={editReturnForm.return_received_date} onChange={(e) => setEditReturnForm({...editReturnForm, return_received_date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Return Condition</Label>
              <Select value={editReturnForm.return_condition} onValueChange={(v) => setEditReturnForm({...editReturnForm, return_condition: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="correct">Correct Return</SelectItem>
                  <SelectItem value="false">False / Wrong Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editReturnForm.return_condition === 'false' && (
              <div>
                <Label>Reason</Label>
                <Select value={editReturnForm.false_return_reason} onValueChange={(v) => setEditReturnForm({...editReturnForm, false_return_reason: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wrong_product">Wrong Product</SelectItem>
                    <SelectItem value="used">Used Product</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="short_qty">Short Quantity</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Input value={editReturnForm.notes} onChange={(e) => setEditReturnForm({...editReturnForm, notes: e.target.value})} placeholder="Notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditReturnDialog(false)}>Cancel</Button>
            <Button onClick={handleEditReturn} data-testid="save-edit-return-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
